import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Checkroom as CheckroomIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Sync as SyncIcon
} from '@mui/icons-material';

interface ClothingAssignment {
  id: string;
  item_type: string;
  size: string;
  color: string;
  guest_id: string;
  status: string;
}

interface InventoryItem {
  id?: string;
  item_type: string;
  size: string;
  color: string;
  total_quantity: number;
  allocated_quantity: number;
  available_quantity: number;
  status: 'surplus' | 'exact' | 'short';
  assigned_guests?: string[];
  assignments?: ClothingAssignment[];
}

interface GuestInfo {
  id: string;
  full_name: string;
  email: string;
}

export const ClothingInventoryManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [hasInventory, setHasInventory] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Assigned guests dialog state
  const [guestsDialogOpen, setGuestsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [assignedGuestsInfo, setAssignedGuestsInfo] = useState<GuestInfo[]>([]);
  const [loadingGuests, setLoadingGuests] = useState(false);

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, discover items from assignments
      const assignmentsResponse = await fetch(
        'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/admin/clothing-assignments',
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!assignmentsResponse.ok) {
        throw new Error('Failed to load clothing assignments');
      }

      const assignmentsData = await assignmentsResponse.json();
      const assignments: ClothingAssignment[] = assignmentsData.assignments || [];

      // Group assignments by item type + size + color
      const itemMap = new Map<string, InventoryItem>();
      
      assignments.forEach(assignment => {
        const key = `${assignment.item_type}-${assignment.size}-${assignment.color}`;
        
        if (!itemMap.has(key)) {
          itemMap.set(key, {
            item_type: assignment.item_type,
            size: assignment.size,
            color: assignment.color,
            total_quantity: 0,
            allocated_quantity: 0,
            available_quantity: 0,
            status: 'short',
            assigned_guests: [],
            assignments: []
          });
        }
        
        const item = itemMap.get(key)!;
        item.allocated_quantity++;
        item.assigned_guests!.push(assignment.guest_id);
        item.assignments!.push(assignment);
      });

      // Try to load existing inventory counts
      try {
        const inventoryResponse = await fetch(
          'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/inventory/admin',
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (inventoryResponse.ok) {
          const inventoryData = await inventoryResponse.json();
          const existingInventory = inventoryData.items || [];
          
          if (existingInventory.length > 0) {
            setHasInventory(true);
            
            // Merge with discovered items
            itemMap.forEach((discoveredItem, key) => {
              const existing = existingInventory.find((inv: any) => 
                inv.item_type === discoveredItem.item_type &&
                inv.size === discoveredItem.size &&
                inv.color === discoveredItem.color
              );
              
              if (existing) {
                discoveredItem.id = existing.id;
                discoveredItem.total_quantity = existing.total_quantity;
                discoveredItem.available_quantity = existing.total_quantity - discoveredItem.allocated_quantity;
                discoveredItem.status = calculateStatus(
                  existing.total_quantity,
                  discoveredItem.allocated_quantity
                );
              }
            });
          }
        }
      } catch (invError) {
        console.log('No existing inventory found, will need to set up');
      }

      const items = Array.from(itemMap.values());
      
      // Sort by item type, then color, then size
      items.sort((a, b) => {
        // First by item type
        const typeA = a.item_type || '';
        const typeB = b.item_type || '';
        if (typeA !== typeB) {
          return typeA.localeCompare(typeB);
        }
        
        // Then by color (handle null values)
        const colorA = a.color || '';
        const colorB = b.color || '';
        if (colorA !== colorB) {
          return colorA.localeCompare(colorB);
        }
        
        // Finally by size (with special handling for common sizes)
        const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        const sizeA = (a.size || '').toUpperCase();
        const sizeB = (b.size || '').toUpperCase();
        const aIndex = sizeOrder.indexOf(sizeA);
        const bIndex = sizeOrder.indexOf(sizeB);
        
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        return sizeA.localeCompare(sizeB);
      });
      
      setInventoryItems(items);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatus = (total: number, allocated: number): 'surplus' | 'exact' | 'short' => {
    const available = total - allocated;
    if (available < 0) return 'short';
    if (available === 0) return 'exact';
    return 'surplus';
  };

  const handleEditQuantity = (item: InventoryItem) => {
    setEditingItem(item);
    setEditQuantity(item.total_quantity.toString());
    setEditDialogOpen(true);
  };

  const handleSaveQuantity = async () => {
    if (!editingItem) return;

    try {
      setEditLoading(true);
      setError(null);

      const newTotal = parseInt(editQuantity);
      if (isNaN(newTotal) || newTotal < 0) {
        throw new Error('Please enter a valid quantity');
      }

      // Save to backend
      const response = await fetch(
        'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/inventory/admin',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            item_type: editingItem.item_type,
            size: editingItem.size,
            color: editingItem.color,
            total_quantity: newTotal,
            allocated_quantity: editingItem.allocated_quantity
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save inventory');
      }

      setSuccess('Inventory updated successfully');
      setEditDialogOpen(false);
      setHasInventory(true);
      await loadInventory();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save inventory');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSetupInventory = () => {
    // Open dialog to set all quantities at once
    setSuccess('Click the edit icon next to each item to set your inventory count');
  };

  const handleViewAssignedGuests = async (item: InventoryItem) => {
    setSelectedItem(item);
    setGuestsDialogOpen(true);
    setLoadingGuests(true);
    setAssignedGuestsInfo([]);

    try {
      // Use compatibility service to fetch guests
      const { CompatibilityService } = await import('../services/compatibility');
      const compatibilityService = CompatibilityService.getInstance({
        useUnifiedApi: false,
        legacyFormat: true
      });
      const data = await compatibilityService.getGuests({});
      
      if (data.success) {
        const allGuests = data.data.guests || data.data || [];
        const guestIds = item.assigned_guests || [];
        
        // Filter to only guests assigned to this item
        const assignedGuests = allGuests.filter((guest: any) => 
          guestIds.includes(guest.id)
        );
        
        setAssignedGuestsInfo(assignedGuests);
      } else {
        throw new Error('Failed to fetch guests');
      }
    } catch (err) {
      console.error('Failed to load guest details:', err);
      setError('Failed to load guest information');
    } finally {
      setLoadingGuests(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'surplus':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'exact':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'short':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (item: InventoryItem) => {
    const available = item.total_quantity - item.allocated_quantity;
    
    if (item.status === 'short') {
      return (
        <Chip
          label={`SHORT by ${Math.abs(available)}`}
          color="error"
          size="small"
          icon={<ErrorIcon />}
        />
      );
    } else if (item.status === 'exact') {
      return (
        <Chip
          label="No Buffer"
          color="warning"
          size="small"
          icon={<WarningIcon />}
        />
      );
    } else {
      return (
        <Chip
          label={`${available} Extra`}
          color="success"
          size="small"
          icon={<CheckCircleIcon />}
        />
      );
    }
  };

  const summary = {
    total: inventoryItems.length,
    surplus: inventoryItems.filter(i => i.status === 'surplus').length,
    exact: inventoryItems.filter(i => i.status === 'exact').length,
    short: inventoryItems.filter(i => i.status === 'short').length,
    totalAssigned: inventoryItems.reduce((sum, i) => sum + i.allocated_quantity, 0),
    totalAvailable: inventoryItems.reduce((sum, i) => sum + (i.total_quantity - i.allocated_quantity), 0)
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CheckroomIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" component="h2">
              Clothing Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track clothing assignments vs physical inventory
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!hasInventory && inventoryItems.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={handleSetupInventory}
            >
              Set Up Inventory
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadInventory}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!hasInventory && inventoryItems.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Inventory Not Set Up</strong>
          </Typography>
          <Typography variant="body2">
            Found {inventoryItems.length} clothing items from guest assignments. 
            Click "Set Up Inventory" or edit each item to enter your physical inventory counts.
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      {hasInventory && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
              <Typography variant="h4">{summary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
              <Typography variant="body2" color="text.secondary">
                Surplus Stock
              </Typography>
              <Typography variant="h4">{summary.surplus}</Typography>
              <Typography variant="caption">
                {summary.totalAvailable > 0 ? `${summary.totalAvailable} extra pieces` : ''}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
              <Typography variant="body2" color="text.secondary">
                No Buffer
              </Typography>
              <Typography variant="h4">{summary.exact}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
              <Typography variant="body2" color="text.secondary">
                Short
              </Typography>
              <Typography variant="h4">{summary.short}</Typography>
              <Typography variant="caption">Action needed</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Inventory Table */}
      {inventoryItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckroomIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Clothing Assignments Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Clothing inventory will appear here once guests have been assigned clothing items.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Color</TableCell>
                <TableCell align="center">Assigned</TableCell>
                <TableCell align="center">Physical Stock</TableCell>
                <TableCell align="center">Available</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventoryItems.map((item, index) => {
                const available = item.total_quantity - item.allocated_quantity;
                return (
                  <TableRow key={`${item.item_type}-${item.size}-${item.color}`}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.item_type}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.size}</TableCell>
                    <TableCell>{item.color}</TableCell>
                    <TableCell 
                      align="center"
                      onClick={() => item.allocated_quantity > 0 && handleViewAssignedGuests(item)}
                      sx={{ 
                        cursor: item.allocated_quantity > 0 ? 'pointer' : 'default',
                        '&:hover': item.allocated_quantity > 0 ? { bgcolor: 'action.hover' } : {}
                      }}
                    >
                      <Chip
                        label={item.allocated_quantity}
                        size="small"
                        variant="outlined"
                        color={item.allocated_quantity > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {item.total_quantity || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          color: available < 0 ? 'error.main' : available === 0 ? 'warning.main' : 'success.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {available}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {hasInventory ? getStatusChip(item) : (
                        <Chip label="Not Set" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Set inventory count">
                        <IconButton
                          size="small"
                          onClick={() => handleEditQuantity(item)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assigned Guests Dialog */}
      <Dialog 
        open={guestsDialogOpen} 
        onClose={() => setGuestsDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Assigned Guests
          {selectedItem && (
            <Typography variant="body2" color="text.secondary">
              {selectedItem.item_type} - {selectedItem.size} - {selectedItem.color}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingGuests ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : assignedGuestsInfo.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No guests assigned
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {assignedGuestsInfo.length} guest(s) assigned this item:
              </Typography>
              <Box sx={{ mt: 2 }}>
                {assignedGuestsInfo.map((guest, index) => (
                  <Paper key={guest.id} sx={{ p: 2, mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {guest.full_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {guest.email}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuestsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Quantity Dialog */}
      <Dialog open={editDialogOpen} onClose={() => !editLoading && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Set Inventory Count
        </DialogTitle>
        <DialogContent>
          {editingItem && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Item: <strong>{editingItem.item_type}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Size: <strong>{editingItem.size}</strong> | Color: <strong>{editingItem.color}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Currently assigned to: <strong>{editingItem.allocated_quantity} guest(s)</strong>
              </Typography>

              <TextField
                fullWidth
                label="Physical Inventory Count"
                type="number"
                value={editQuantity}
                onChange={(e) => setEditQuantity(e.target.value)}
                helperText={`Enter how many you have in stock (minimum: ${editingItem.allocated_quantity})`}
                inputProps={{ min: editingItem.allocated_quantity }}
                autoFocus
              />

              {editQuantity && parseInt(editQuantity) >= 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Preview:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Physical Stock: {editQuantity}
                  </Typography>
                  <Typography variant="body2">
                    Assigned: {editingItem.allocated_quantity}
                  </Typography>
                  <Typography variant="body2">
                    Available: {parseInt(editQuantity) - editingItem.allocated_quantity}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {calculateStatus(parseInt(editQuantity), editingItem.allocated_quantity) === 'short' && (
                      <Chip label="SHORT - Need more!" color="error" size="small" />
                    )}
                    {calculateStatus(parseInt(editQuantity), editingItem.allocated_quantity) === 'exact' && (
                      <Chip label="Exact match - No buffer" color="warning" size="small" />
                    )}
                    {calculateStatus(parseInt(editQuantity), editingItem.allocated_quantity) === 'surplus' && (
                      <Chip label="Good - Have extras" color="success" size="small" />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveQuantity}
            variant="contained"
            disabled={editLoading || !editQuantity}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
